import { AttachFile } from '@mui/icons-material';
import { Box, Divider, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useEffect, useState } from 'react';
import {
  DbLocation,
  HandlersAttachmentResponse,
  HandlersDeliverableTypeWithProjectsResponse,
  HandlersProjectResponse,
  useGetApiDeliverablesTypesQuery,
  useLazyGetApiLocationsByIdQuery,
  useLazyGetApiProjectsByIdQuery,
} from '../../../../../app/athenaApiGen';
import { useDeliverableModalContext } from '../DeliverableModalContext';
import { formatFileSize } from '../formatFileSize';
dayjs.extend(utc);

const Field = ({ label, value }: { label: string; value?: string | null }) => (
  <Typography variant="body2" sx={{ wordBreak: 'break-word', lineHeight: 1.5 }}>
    <Box component="span" sx={{ color: 'text.secondary' }}>
      {label}:{' '}
    </Box>
    <Box component="span" sx={{ fontWeight: 500 }}>
      {value && value.length > 0 ? value : '—'}
    </Box>
  </Typography>
);

const DeliverableSummary = () => {
  const { deliverable, uploadedFiles, savedFiles } =
    useDeliverableModalContext();
  const [location, setLocation] = useState<DbLocation | undefined>(undefined);
  const [project, setProject] = useState<HandlersProjectResponse | undefined>(
    undefined,
  );
  const [type, setType] = useState<
    HandlersDeliverableTypeWithProjectsResponse | undefined
  >(undefined);
  const [fetchLocation] = useLazyGetApiLocationsByIdQuery();
  const [fetchProject] = useLazyGetApiProjectsByIdQuery();
  const { data } = useGetApiDeliverablesTypesQuery();

  useEffect(() => {
    if (deliverable?.locationId) {
      fetchLocation({
        id: deliverable.locationId ?? '',
      })
        .unwrap()
        .then((data) => {
          setLocation(data);
        });
    }

    if (deliverable?.projectId) {
      fetchProject({
        id: deliverable.projectId ?? '',
      })
        .unwrap()
        .then((data) => {
          setProject(data);
        });
    }

    if (deliverable?.deliverableTypeId) {
      setType(
        data?.find(
          (type) => type.deliverableTypeId === deliverable.deliverableTypeId,
        ),
      );
    }
  }, [
    deliverable?.locationId,
    deliverable?.deliverableTypeId,
    deliverable?.projectId,
  ]);

  const attachments = [...savedFiles, ...uploadedFiles];
  const dueDate = deliverable.dueDate
    ? dayjs(deliverable.dueDate).utc().format('DD MMM YYYY')
    : null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        gap: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          flexShrink: 0,
        }}
      >
        <Field label="Title" value={deliverable.deliverableTitle} />
        <Field label="Project" value={project?.title} />
        <Field label="Due Date" value={dueDate} />
        <Field label="Workflow Type" value={deliverable.workflowType} />
        <Field label="Location" value={location?.name} />
        <Field label="Deliverable Type" value={type?.title} />
        <Field label="Description" value={deliverable.description} />
      </Box>

      {attachments.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            overflow: 'hidden',
            minHeight: 0,
            mt: 'auto',
          }}
        >
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                mb: 1,
                color: 'text.secondary',
              }}
            >
              <AttachFile fontSize="small" />
              <Typography variant="subtitle2">
                Attachments ({attachments.length})
              </Typography>
            </Box>
            <Divider />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              minHeight: 0,
            }}
          >
            {attachments.map((file, index) => {
              const name = (file as { name?: string })?.name ?? '';
              const sizeBytes =
                (file as File)?.size ??
                (file as HandlersAttachmentResponse)?.fileSize;
              const size = formatFileSize(sizeBytes);
              return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.5,
                    minWidth: 0,
                  }}
                >
                  <Tooltip title={name}>
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {name}
                    </Typography>
                  </Tooltip>
                  {size && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      {size}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DeliverableSummary;
